export class Modal {
  constructor(identifier, context) {
    this.node = document.querySelector(`#${identifier}`);
    this.closeBtn = document.querySelector(`#${identifier}-close`);
    this.content = this.node.querySelector(`#${identifier}-content`);
    this.scrollPosition = 0;
    // Built-in features
    this.cleanContentFeature = true;
    this.animationFeature = true;
    // Go over if there is disabled built-in features
    if (typeof context !== 'undefined' && context.cleanContent === false) {
      this.cleanContentFeature = false;
    }
    if (typeof context !== 'undefined' && context.animation === false) {
      this.animationFeature = false;
    }
  }

  /**
   * Set HTML content that you want to render into modal
   * @param {HTML} content - HTML or HTML String content
   * @returns Modal
   */
  setContent(content) {
    this.content.innerHTML = content;
    return this;
  }

  /**
   * Its main purpose will be delay to add a class which will trigger an animation on dialog
   * @param {HTMLElement} element - element where something will be set
   * @param {number} ms - delay number in miliseconds
   * @returns {Promise} - function will assign a class on dialog and trigger an animation
   */
  #delayOpenAnimation(element, ms) {
    return new Promise((resolve) => {
      setTimeout(() => {
        element.classList.toggle('open-animation', document.body.offsetWidth < 786);
        element.classList.remove('close-animation');
        return resolve;
      }, ms);
    });
  }

  /**
   * Its main purpose will be delay to remove a class which will trigger an animation on dialog
   * @param {HTMLElement} element - element where something will be set
   * @param {number} ms - delay number in miliseconds
   * @returns {Promise} - function will assign a class on dialog and trigger an animation
   */
  #delayCloseAnimation(element, ms) {
    return new Promise((resolve) => {
      element.classList.remove('open-animation');
      if (document.body.offsetWidth < 786) {
        setTimeout(() => {
          return resolve();
        }, ms);
      } else {
        return resolve();
      }
    });
  }

  /**
   * Open Modal
   */
  show() {
    this.previouslyFocused = document.activeElement;
    this.scrollPosition = window.scrollY;
    this.node.showModal();
    this.#delayOpenAnimation(this.node, 5);

    document.documentElement.style.overflow = 'hidden';
  }

  /**
   * Close Modal Logic
   * @param {function} callback - function that it will invoke once it finished to close modal
   */
  #closeModalClickFlow(callback) {
    if (this.cleanContentFeature) {
      this.content.innerHTML = '';
    }
    document.documentElement.style.overflow = '';
    window.scrollTo(0, this.scrollPosition);
    callback();

    // Desktop experience doesn't have animation, so we can just close modal
    if (document.body.offsetWidth > 786) {
      this.node.close();
      this.previouslyFocused?.focus();
      return;
    }

    if (this.animationFeature) {
      this.node.classList.toggle('close-animation', document.body.offsetWidth < 786);
      this.node.addEventListener(
        'animationend',
        () => {
          this.node.classList.remove('close-animation');
          this.node.close();
          this.previouslyFocused?.focus();
        },
        { once: true },
      );
    } else {
      // If animation is disabled, just close modal without animation. Special case for mobile devices.
      this.node.close();
      this.previouslyFocused?.focus();
    }
  }

  /**
   * Handler that will gracefully close your modal
   * @param {function} callback - function that it will invoke once it finished to close modal
   */
  handleCloseModalClick(callback) {
    if (!this.animationFeature) {
      return this.#closeModalClickFlow(callback);
    }
    this.#delayCloseAnimation(this.node, 250).then(() => {
      return this.#closeModalClickFlow(callback);
    });
  }

  /**
   * Set event listners are charged to close Modal
   * @param {function} callback - function to execute after modal closes
   */
  setClose(callback = () => {}) {
    // Add event listener to close button only if it exists
    this.closeBtn?.addEventListener('click', () => this.handleCloseModalClick(callback));
    // Always add event listener to the modal backdrop
    this.node.addEventListener(
      'click',
      (e) => (e.target == this.node || e.target.ariaLabel === 'close modal') && this.handleCloseModalClick(callback),
    );
  }
}
